import { Footer } from "@/components/Footer";
import { PageMeta } from "@/components/site/blocks";
import { Band, Closing, Faqs, Hero, In, List, P, Related, Src, Steps, Table, WhatIs, WorkflowCard } from "@/components/seo/kit";

// /integrations/gmail-to-slack. Plan: gtm/products/clearmakes-inbox/seo/pages/gmail-to-slack.md
// Competitor facts opened on 2026-09-29; sources are linked on the page.

const PATH = "/integrations/gmail-to-slack";

const faqs = [
  {
    q: "Can I send only some Gmail emails to Slack?",
    a: "Yes. Say which ones in the sentence, for example “only emails from customers” or “only emails that mention an invoice”. Inbox adds a check step, and emails that don't match stop there without posting anything.",
  },
  {
    q: "Does this need a paid Slack plan?",
    a: "No. Inbox posts through the Slack app you connect, and Slack's free plan allows up to 10 apps. Slack's own “send emails to this channel” address is only on paid Slack plans.",
  },
  {
    q: "Will the whole email appear in Slack?",
    a: "Only what you ask for. Most people post the sender, the subject and a two-line summary with a link back to Gmail, so private details stay in the mailbox. You can ask for the full text instead.",
  },
  {
    q: "What does it cost?",
    a: "The free plan covers 3 connected apps, 2 new workflows a month and 100 runs a month, enough to try Gmail to Slack. Starter is $29 a month for 5,000 runs. Each email that starts the workflow is one run.",
  },
  {
    q: "Can it post to more than one channel?",
    a: "Yes. One workflow can check what an email is about and post sales emails to #sales and support emails to #support. Describe both in the sentence and check the steps before you switch it on.",
  },
];

const GmailToSlack = () => (
  <>
    <PageMeta
      title="Gmail to Slack Automation: Post Emails to a Channel"
      description="Post new Gmail emails to the right Slack channel with a short summary, set up in one sentence. Steps, permissions, limits and a free plan to try it."
      path={PATH}
      crumbs={[
        { name: "Home", path: "/" },
        { name: "Integrations", path: "/integrations" },
        { name: "Gmail to Slack", path: PATH },
      ]}
    />
    <main>
      <Hero
        eyebrow="Integrations · Gmail and Slack"
        title="Gmail to Slack automation, set up in one sentence"
        lead="When an important email lands in Gmail, post it to the right Slack channel with a short summary, so the team sees it without anyone forwarding it."
        secondary={{ label: "See every integration", href: "/integrations" }}
      />

      <WhatIs title="What is the Gmail to Slack workflow in ClearMakes Inbox?">
        It is a workflow in ClearMakes Inbox that watches your Gmail and, when a new email matches what you described, posts it to a Slack
        channel or a direct message. You describe it in one sentence; Inbox writes the steps for you to check. It works on the free plan, and
        paid plans start at $29 a month.
      </WhatIs>

      <Band
        tone="pale"
        title="What the Gmail to Slack workflow does"
        answer="It turns an email into a Slack post, but only the emails you care about. You choose which emails count, which channel they go to, and what the post says."
      >
        <div className="grid gap-5 lg:grid-cols-2">
          <WorkflowCard
            sentence="When a customer emails support@, post the sender, subject and a two-line summary to #support in Slack."
            steps={["When an email arrives in Gmail", "Check it is from a customer", "Summarise it", "Post to #support in Slack"]}
          />
          <WorkflowCard
            sentence="When an email mentions an invoice or a payment, tell #finance and add the label Billing."
            steps={["When an email arrives in Gmail", "Stop if it is not about invoices or payments", "Add the label Billing", "Post to #finance in Slack"]}
          />
        </div>
        <P>
          Each example is written the way you would type it. Inbox turns the sentence into steps using the step names shown above, and asks
          at most one question if it needs to, for example whether a reply should go out by itself or wait for you.
        </P>
      </Band>

      <Band
        tone="white"
        title="How to set up Gmail to Slack"
        answer="Connect the two apps, type one sentence, check the steps, and switch it on. You need a Google account with Gmail and a Slack workspace where you can add apps."
      >
        <Steps
          items={[
            { title: "Create a free ClearMakes account", body: <>Start at <In href="/products/inbox">ClearMakes Inbox</In>. The free plan is enough to try this.</> },
            { title: "Connect Gmail", body: "Open Apps, find Gmail and choose Connect. Google's own sign-in screen lists the permissions before you agree." },
            { title: "Connect Slack", body: "Do the same for Slack and pick the workspace. If your workspace only lets admins add apps, ask an admin first." },
            { title: "Describe the workflow", body: "Open Workflows, choose to create one, and type what should happen, for example the support example above." },
            { title: "Answer at most one question", body: "If Inbox needs to know something about behaviour, it asks one thing first. It never asks you to write the wording." },
            { title: "Check the steps and switch it on", body: "Read the steps, change any of them, then create the workflow and switch it on." },
            { title: "Watch the first runs", body: "Every run appears in your activity with each step and what was posted, so you can see it working, or see where it stopped." },
          ]}
        />
      </Band>

      <Band
        tone="pale"
        title="What Inbox can read and send"
        answer="Inbox reads only the emails your workflows need and posts only what your workflows say to post. You sign in to Gmail and Slack through Composio, our integration provider, and can disconnect either one at any time."
        narrow
      >
        <List
          items={[
            "Gmail: new emails in the connected mailbox, so a workflow can check and summarise them. Labels and drafts only if your workflow adds them.",
            "Slack: posting to the channels or people your workflow names. This workflow only posts; it does not need to read your Slack messages.",
            "Your data is encrypted in transit and at rest, and never used to train AI models. See how ClearMakes Inbox uses connected apps on the product page.",
            "Google and Slack each show the exact permissions on their own sign-in screens before you agree.",
          ]}
        />
        <P>
          More detail: <In href="/security-architecture">security and your data</In> and the <In href="/privacy">privacy policy</In>.
        </P>
      </Band>

      <Band
        tone="white"
        title="Other ways to get Gmail into Slack"
        answer="You don't need us for a simple copy of every email. Slack's own email address and a Gmail forwarding rule do that on a paid Slack plan; automation tools are for choosing which emails go where."
      >
        <Table
          caption="Ways to send Gmail emails to Slack"
          head={["Way", "What it costs", "Good at", "Watch out for"]}
          rows={[
            [
              "Slack's email address for a channel + a Gmail forwarding filter",
              "Included in paid Slack plans",
              "Copying every matching email into a channel, no extra tool",
              "Paid Slack plans only; no summary; you manage Gmail filters by hand",
            ],
            [
              "Zapier (Gmail + Slack)",
              "Free: 100 tasks a month, two-step Zaps, checks every 15 min. Professional: $29.99 a month for 750 tasks",
              "Huge app list, many Gmail triggers",
              "Each successful action is a task, so multi-step Zaps use more",
            ],
            [
              "Make",
              "Free: 1,000 credits, 2 active scenarios, 15-min interval. Paid from $9 a month for 5,000 credits",
              "Low price for high volume, visual builder",
              "You build it on a canvas; one operation is one credit",
            ],
            [
              "ClearMakes Inbox",
              "Free: 3 apps, 2 new workflows and 100 runs a month. Starter: $29 a month for 5,000 runs",
              "One sentence to set up; summaries, checks, delays and wait-for-reply in the same workflow",
              "Fewer ready-made templates than Zapier; newer company",
            ],
          ]}
          note={
            <>
              Checked on 29 September 2026: <Src href="https://slack.com/help/articles/206819278-Send-emails-to-Slack">Slack, Send emails to Slack</Src>,{" "}
              <Src href="https://zapier.com/pricing">Zapier pricing</Src>, <Src href="https://www.make.com/en/pricing">Make pricing</Src>. Prices change;
              check each page before you buy. A Zapier task is one action; a Make credit is one operation; an Inbox run is one workflow start
              with all its steps.
            </>
          }
        />
        <P>
          Want the step-by-step for the free methods? Read <In href="/guides/forward-email-to-slack-channel">how to forward emails to a Slack channel</In>.
        </P>
      </Band>

      <Band
        tone="pale"
        title="Limits, and who this is not for"
        answer="If you want every email copied into Slack word for word, use Slack's own email address on a paid plan. Inbox is for choosing which emails matter and saying something useful about them."
        narrow
      >
        <List
          items={[
            "Not a shared inbox. Your team still replies from Gmail; Slack is where they see what came in. If you need assignments and shared replies, look at a shared inbox tool.",
            "AI summaries and checks can be wrong now and then. Check the first runs, and keep sensitive decisions with a person.",
            "The free plan has 100 runs a month: fine for a test, not for a busy support mailbox.",
            "How quickly a post appears after an email arrives depends on Gmail and our integration provider; we have not published a measured figure yet.",
          ]}
        />
      </Band>

      <Faqs items={faqs} />

      <Related
        links={[
          { href: "/integrations/outlook-to-slack", label: "Outlook to Slack", desc: "The same workflow for Outlook and Microsoft 365 mailboxes." },
          { href: "/guides/forward-email-to-slack-channel", label: "Forward emails to a Slack channel", desc: "Every way to do it, free ones first." },
          { href: "/gmail-integration", label: "Gmail automation", desc: "Everything a workflow can do when an email arrives in Gmail." },
          { href: "/slack-integration", label: "Slack automation", desc: "Posts, direct messages and channels your workflows can use." },
          { href: "/alternatives/zapier", label: "Zapier alternative for email", desc: "Prices and fit compared, with sources." },
          { href: "/pricing", label: "ClearMakes Inbox pricing", desc: "Free, Starter, Growth and Enterprise." },
        ]}
      />

      <Closing title="Post the emails that matter to Slack." line="Connect Gmail and Slack, type one sentence, and check the steps." />
    </main>
    <Footer />
  </>
);

export default GmailToSlack;
